import {
  type ColumnFiltersState,
  type ColumnPinningState,
  functionalUpdate,
  type PaginationState,
  type RowData,
  type SortingState,
  type TableOptions,
  type Updater,
  useTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import type { TableProps } from '../Table';
import { kkTableFeatures } from '../tableFeatures';

type KkTableOptions<TData extends RowData> = TableOptions<
  typeof kkTableFeatures,
  TData
>;

function useTableFilters(
  controlledFilters: ColumnFiltersState | undefined,
  onFilterChange?: (filters: ColumnFiltersState) => void,
) {
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);

  const filters = controlledFilters ?? internalColumnFilters;
  // v8 allowed persisted empty selections; remove this when those session states no longer need restoration.
  const activeFilters = useMemo(() => {
    const normalizedFilters = filters.filter(
      ({ value }) => !Array.isArray(value) || value.length > 0,
    );
    return normalizedFilters.length === filters.length
      ? filters
      : normalizedFilters;
  }, [filters]);

  const handleFilterChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      const nextFilters = functionalUpdate(updater, activeFilters);
      if (onFilterChange) {
        onFilterChange(nextFilters);
      } else {
        setInternalColumnFilters(nextFilters);
      }
    },
    [activeFilters, onFilterChange],
  );

  return { columnFilters: activeFilters, handleFilterChange };
}

function useTableState<TData extends RowData, TDisplayKeys extends string>(
  props: TableProps<TData, TDisplayKeys>,
) {
  const [sorting, setSorting] = useState<SortingState>(
    props.initialSorting ?? [],
  );
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    props.pinning
      ? {
          start: props.pinning.left.map(String),
          end: props.pinning.right.map(String),
        }
      : { start: [], end: [] },
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: props.pageSize ?? 12,
  });

  const { columnFilters, handleFilterChange } = useTableFilters(
    props.filters,
    props.onFilterChange,
  );
  const rowSelection = useMemo<Record<string, true>>(() => {
    const selectedRowId = props.rowSelection?.selectedRowId;
    return selectedRowId !== null && selectedRowId !== undefined
      ? { [selectedRowId]: true }
      : {};
  }, [props.rowSelection?.selectedRowId]);

  const tableState = useMemo(
    () => ({
      sorting,
      pagination,
      columnPinning,
      columnFilters,
      columnVisibility: props.columnVisibility,
      columnOrder: props.columnOrder,
      rowSelection,
    }),
    [
      columnFilters,
      columnPinning,
      pagination,
      props.columnOrder,
      props.columnVisibility,
      rowSelection,
      sorting,
    ],
  );

  return {
    handleFilterChange,
    setColumnPinning,
    setPagination,
    setSorting,
    tableState,
  };
}

function getFilteringOptions<TData extends RowData>(
  filterable: boolean | undefined,
  onColumnFiltersChange: (updater: Updater<ColumnFiltersState>) => void,
): Partial<KkTableOptions<TData>> {
  if (!filterable) return { enableColumnFilters: false };

  return { onColumnFiltersChange };
}

function getPinningOptions<TData extends RowData, TDisplayKeys extends string>(
  pinning: TableProps<TData, TDisplayKeys>['pinning'],
  onColumnPinningChange: (value: ColumnPinningState) => void,
): Partial<KkTableOptions<TData>> {
  if (!pinning) return { enableColumnPinning: false };

  return { onColumnPinningChange };
}

function getSortingOptions<TData extends RowData>(
  sortable: boolean | undefined,
  sortDescFirst: boolean | undefined,
  onSortingChange: (value: SortingState) => void,
): Partial<KkTableOptions<TData>> {
  if (!sortable) return {};

  return {
    onSortingChange,
    sortDescFirst: sortDescFirst ?? false,
  };
}

function getPaginationOptions<TData extends RowData>(
  paginated: boolean | undefined,
  manualPagination: boolean | undefined,
  pageCount: number | undefined,
  onPaginationChange: (value: PaginationState) => void,
): Partial<KkTableOptions<TData>> {
  if (!paginated) return { manualPagination: true };

  return {
    manualPagination,
    onPaginationChange,
    pageCount: manualPagination ? pageCount : undefined,
  };
}

export function useTableInstance<
  TData extends RowData,
  TDisplayKeys extends string,
>(props: TableProps<TData, TDisplayKeys>) {
  const {
    handleFilterChange,
    setColumnPinning,
    setPagination,
    setSorting,
    tableState,
  } = useTableState(props);

  return useTable<typeof kkTableFeatures, TData>({
    features: kkTableFeatures,
    columns: props.columns,
    data: props.data,
    enableMultiRowSelection: false,
    enableRowSelection: Boolean(props.rowSelection),
    getRowId: props.rowSelection?.getRowId,
    state: tableState,
    enableSortingRemoval: props.enableSortingRemoval,
    ...getFilteringOptions(props.filterable, handleFilterChange),
    ...getPinningOptions(props.pinning, setColumnPinning),
    ...getSortingOptions(props.sortable, props.sortDescFirst, setSorting),
    ...getPaginationOptions(
      props.paginated,
      props.manualPagination,
      props.pageCount,
      setPagination,
    ),
  });
}
